import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  Bookmark,
  BriefcaseBusiness,
  CircleHelp,
  ExternalLink,
  MapPin,
  Play,
  Plus,
  RefreshCw,
  Sparkles,
  ThumbsUp,
  X,
} from 'lucide-react';
import {useMemo, useState, type FormEvent} from 'react';
import {api} from '../api';
import {useWorkOperation} from '../hooks/useWorkOperation';
import {Badge, PrimaryButton, QuietButton, SecondaryButton, Surface, TextInput, Textarea} from '../design';
import {DiscoveryQuestionAnswer, DiscoveryResult} from '../types';
import {EmptyPanel, ErrorPanel, LoadingPanel, PageHeader, formatDateTime, formatRelativeDate} from '../components/PagePrimitives';

type SourceFilter = 'all' | 'linkedin' | 'indeed' | 'manual';
type ReviewFilter = 'new' | 'reviewed';
type DiscoverySource = 'linkedin' | 'indeed';
const SOURCE_LABELS: Record<SourceFilter, string> = {
  all: 'All sources',
  linkedin: 'LinkedIn',
  indeed: 'Indeed',
  manual: 'Added by you',
};
const EMPTY_MANUAL_POSTING = {
  canonicalUrl: '',
  companyName: '',
  title: '',
  description: '',
  location: '',
  compensation: '',
};
type DiscoverySourceHealth = {
  state?: string;
  lastSuccessfulAt?: string;
  lastAttemptAt?: string;
  message?: string;
};

export const DiscoverPage = () => {
  const queryClient = useQueryClient();
  const [source, setSource] = useState<SourceFilter>('all');
  const [review, setReview] = useState<ReviewFilter>('new');
  const [askTargetId, setAskTargetId] = useState<string | null>(null);
  const [question, setQuestion] = useState('');
  const [answers, setAnswers] = useState<ReadonlyMap<string, DiscoveryQuestionAnswer>>(() => new Map());
  const [operationId, setOperationId] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualPosting, setManualPosting] = useState(EMPTY_MANUAL_POSTING);
  const results = useQuery({queryKey: ['discovery-results'], queryFn: api.discoveryResults});
  const bootstrap = useQuery({queryKey: ['bootstrap'], queryFn: api.bootstrap});
  const integrations = useQuery({queryKey: ['integrations'], queryFn: api.integrations});
  const discoveryCapability = bootstrap.data?.capabilities?.discovery;

  const run = useMutation({
    mutationFn: api.runDiscovery,
    onSuccess: (result) => setOperationId(result.operationId),
  });
  const operationInvalidations = useMemo(() => [
    ['discovery-results'],
    ['integrations'],
    ['bootstrap'],
  ] as const, []);
  const operation = useWorkOperation(operationId, operationInvalidations);
  const operationActive = operation.data?.state === 'queued' || operation.data?.state === 'running';

  const triage = useMutation({
    mutationFn: ({id, state}: {id: string; state: DiscoveryResult['state']}) =>
      api.triageDiscovery(id, state),
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: ['discovery-results']});
      await queryClient.invalidateQueries({queryKey: ['opportunities']});
      await queryClient.invalidateQueries({queryKey: ['bootstrap']});
    },
  });
  const convert = useMutation({
    mutationFn: (id: string) => api.convertDiscoveryResult(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({queryKey: ['discovery-results']}),
        queryClient.invalidateQueries({queryKey: ['opportunities']}),
        queryClient.invalidateQueries({queryKey: ['action-items']}),
        queryClient.invalidateQueries({queryKey: ['bootstrap']}),
      ]);
    },
  });

  const importManually = useMutation({
    mutationFn: () => api.importManualPosting({
      canonicalUrl: manualPosting.canonicalUrl.trim(),
      companyName: manualPosting.companyName.trim(),
      title: manualPosting.title.trim(),
      description: manualPosting.description.trim(),
      ...(manualPosting.location.trim() ? {location: manualPosting.location.trim()} : {}),
      ...(manualPosting.compensation.trim() ? {compensation: manualPosting.compensation.trim()} : {}),
    }),
    onSuccess: async () => {
      setManualPosting(EMPTY_MANUAL_POSTING);
      setManualOpen(false);
      setSource('manual');
      setReview('new');
      await Promise.all([
        queryClient.invalidateQueries({queryKey: ['discovery-results']}),
        queryClient.invalidateQueries({queryKey: ['bootstrap']}),
      ]);
    },
  });

  const ask = useMutation({
    mutationFn: ({id, question: submitted}: {id: string; question: string}) => api.askDiscoveryResult(id, submitted),
    onSuccess: (answer, input) => {
      setAnswers((current) => new Map(current).set(input.id, answer));
      setAskTargetId(null);
      setQuestion('');
    },
  });


  const visible = useMemo(
    () =>
      (results.data?.items ?? [])
        .filter((item) => item.state !== 'dismissed' && item.state !== 'converted')
        .filter((item) => review === 'new'
          ? item.state === 'new'
          : item.state === 'saved' || item.state === 'interesting')
        .filter((item) => source === 'all' || item.source === source)
        .sort((left, right) => right.fitScore - left.fitScore),
    [results.data, review, source],
  );

  const staleSources = useMemo(() => {
    const discovery = integrations.data?.find((integration) => integration.provider === 'apify') as
      | {sourceStates?: Partial<Record<DiscoverySource, DiscoverySourceHealth>>}
      | undefined;
    return (['linkedin', 'indeed'] as const).flatMap((discoverySource) => {
      const health = discovery?.sourceStates?.[discoverySource];
      if (!health || !['stale', 'blocked', 'error'].includes(String(health.state))) return [];
      return [{
        source: discoverySource,
        label: discoverySource === 'linkedin' ? 'LinkedIn' : 'Indeed',
        health,
      }];
    });
  }, [integrations.data]);

  return (
    <>
      <PageHeader
        eyebrow="Daily shortlist"
        title="Broad enough to surprise you. Selective enough to trust."
        description="Automated results and postings you add yourself arrive here for human triage. Nothing becomes an active opportunity until you decide it deserves one."
        actions={
          <>
            <SecondaryButton
              leftSection={<Plus size={16} aria-hidden="true" />}
              onClick={() => setManualOpen((open) => !open)}
              aria-expanded={manualOpen}
              aria-controls="pd-manual-posting"
            >
              Add a posting
            </SecondaryButton>
            <PrimaryButton
              loading={run.isPending || operationActive}
              disabled={discoveryCapability?.usable === false}
              title={discoveryCapability?.usable === false ? discoveryCapability.message : undefined}
              leftSection={run.isPending || operationActive ? <RefreshCw size={16} aria-hidden="true" /> : <Play size={16} aria-hidden="true" />}
              onClick={() => run.mutate()}
            >
              Run now
            </PrimaryButton>
          </>
        }
      />

      {discoveryCapability?.usable === false ? (
        <div className="pd-stale-banner" role="status">
          <div>
            <strong>Automated job discovery is unavailable.</strong>
            <span>{discoveryCapability.message}</span>
            <span>You can still add a posting you found yourself, and it is scored the same way.</span>
          </div>
          <SecondaryButton onClick={() => setManualOpen(true)}>Add a posting</SecondaryButton>
        </div>
      ) : null}

      {manualOpen ? (
        <Surface id="pd-manual-posting" className="pd-manual-posting">
          <h2>Add a posting you found</h2>
          <p>
            Paste a posting you already opened yourself. PursuitDeck never visits the job
            board for you, and it scores what you paste with the same fit and eligibility
            checks an automated result receives.
          </p>
          <form
            className="pd-modal-form"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              importManually.mutate();
            }}
          >
            <TextInput
              label="Job posting link"
              description="The public link to the posting, so the source stays attached."
              type="url"
              required
              value={manualPosting.canonicalUrl}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setManualPosting((current) => ({...current, canonicalUrl: value}));
              }}
              placeholder="https://boards.example.com/roles/1234"
              maxLength={2_048}
            />
            <TextInput
              label="Company"
              required
              value={manualPosting.companyName}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setManualPosting((current) => ({...current, companyName: value}));
              }}
              placeholder="Example: Acme"
              maxLength={240}
            />
            <TextInput
              label="Role"
              required
              value={manualPosting.title}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setManualPosting((current) => ({...current, title: value}));
              }}
              placeholder="Example: Staff Software Engineer"
              maxLength={240}
            />
            <TextInput
              label="Location"
              value={manualPosting.location}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setManualPosting((current) => ({...current, location: value}));
              }}
              placeholder="Example: Remote (US)"
              maxLength={240}
            />
            <TextInput
              label="Compensation"
              value={manualPosting.compensation}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setManualPosting((current) => ({...current, compensation: value}));
              }}
              placeholder="Example: $210k–$240k + equity"
              maxLength={500}
            />
            <Textarea
              label="Posting text"
              description="Paste the responsibilities and requirements. This is what the fit score reads."
              required
              autosize
              minRows={6}
              maxRows={16}
              value={manualPosting.description}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setManualPosting((current) => ({...current, description: value}));
              }}
              maxLength={50_000}
            />
            {importManually.isError ? (
              <p className="pd-form-error" role="alert">
                {importManually.error instanceof Error ? importManually.error.message : 'The posting could not be added.'}
              </p>
            ) : null}
            <div className="pd-form-actions">
              <SecondaryButton type="button" onClick={() => setManualOpen(false)}>Cancel</SecondaryButton>
              <PrimaryButton type="submit" loading={importManually.isPending}>Add posting</PrimaryButton>
            </div>
          </form>
        </Surface>
      ) : null}

      {run.isSuccess && operation.data?.state !== 'succeeded' ? (
        <div className="pd-inline-status" role="status">
          <Sparkles size={17} aria-hidden="true" />
          {operation.data?.state === 'running'
            ? 'Discovery is running. Results will appear here as each source finishes.'
            : 'Discovery is queued. Results will appear here as each source finishes.'}
        </div>
      ) : null}
      {run.isError || operation.data?.state === 'failed_retryable' || operation.data?.state === 'failed_terminal' ? (
        <div className="pd-stale-banner" role="alert">
          <div>
            <strong>{operation.data?.state === 'failed_terminal' ? 'Discovery needs attention.' : 'Discovery could not finish.'}</strong>
            <span>{operation.data?.message ?? (run.error instanceof Error ? run.error.message : 'The source is temporarily unavailable.')}</span>
          </div>
          {operation.data?.state !== 'failed_terminal' && discoveryCapability?.usable !== false ? <SecondaryButton onClick={() => run.mutate()}>Run again</SecondaryButton> : null}
        </div>
      ) : null}

      {staleSources.map(({source: staleSource, label, health}) => (
        <div className="pd-stale-banner" role="alert" key={staleSource}>
          <div>
            <strong>{label} discovery is stale.</strong>
            <span>
              Last successful synchronization:{' '}
              {health.lastSuccessfulAt ? formatDateTime(health.lastSuccessfulAt) : 'Never'}
            </span>
            <span>
              Failed attempt: {health.lastAttemptAt ? formatDateTime(health.lastAttemptAt) : 'Unknown'}
            </span>
            {health.message ? <span>{health.message}</span> : null}
          </div>
          <SecondaryButton disabled={discoveryCapability?.usable === false} loading={run.isPending || operationActive} onClick={() => run.mutate()}>
            Run again
          </SecondaryButton>
        </div>
      ))}

      <div className="pd-discovery-toolbar">
        <div className="pd-discovery-filters">
          <div className="pd-filter-tabs" role="tablist" aria-label="Review status">
            {(['new', 'reviewed'] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={review === value}
                className={review === value ? 'is-active' : ''}
                onClick={() => setReview(value)}
              >
                {value === 'new' ? 'Needs review' : 'Saved & interesting'}
              </button>
            ))}
          </div>
          <div className="pd-filter-tabs" role="tablist" aria-label="Discovery sources">
            {(['all', 'linkedin', 'indeed', 'manual'] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={source === value}
                className={source === value ? 'is-active' : ''}
                onClick={() => setSource(value)}
              >
                {SOURCE_LABELS[value]}
              </button>
            ))}
          </div>
        </div>
        <span>{visible.length} {review === 'new' ? 'awaiting a decision' : 'kept for later'}</span>
      </div>

      {results.isLoading ? <LoadingPanel label="Loading discovery results" /> : null}
      {results.isError ? <ErrorPanel error={results.error} retry={() => void results.refetch()} /> : null}
      {results.data && visible.length === 0 ? (
        <EmptyPanel
          title={review === 'new'
            ? 'No roles are waiting for review.'
            : 'No saved or interesting roles yet.'}
          description={review === 'new'
            ? 'Run discovery now or confirm a persona query in Career Profile to seed tomorrow’s shortlist.'
            : 'Save or mark a role interesting to keep it available here.'}
          action={
            <PrimaryButton disabled={discoveryCapability?.usable === false} onClick={() => run.mutate()} leftSection={<Play size={16} aria-hidden="true" />}>
              Run discovery
            </PrimaryButton>
          }
        />
      ) : null}

      <div className="pd-discovery-grid">
        {visible.map((result) => (
          <Surface key={result.id} className="pd-discovery-card">
            <div className="pd-discovery-score" aria-label={'Fit score ' + result.fitScore}>
              <strong>{Math.round(result.fitScore)}</strong>
              <span>fit</span>
            </div>
            <div className="pd-discovery-body">
              <div className="pd-card-topline">
                <Badge className={'pd-source-badge source-' + result.source}>{SOURCE_LABELS[result.source]}</Badge>
                <Badge className={'pd-eligibility eligibility-' + result.eligibility}>{result.eligibility}</Badge>
                <span>{formatRelativeDate(result.discoveredAt)}</span>
              </div>
              <h3>{result.title}</h3>
              <p className="pd-company">{result.companyName}</p>
              {result.location ? (
                <p className="pd-location">
                  <MapPin size={15} aria-hidden="true" />
                  {result.location}
                </p>
              ) : null}
              {result.compensation ? <p className="pd-compensation">{result.compensation}</p> : null}
              <p className="pd-fit-summary">{result.fitSummary}</p>
              <a href={result.canonicalUrl} target="_blank" rel="noreferrer" className="pd-source-link">
                View source posting
                <ExternalLink size={14} aria-hidden="true" />
              </a>
              {askTargetId === result.id ? (
                <form
                  className="pd-discovery-question-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const submitted = question.trim();
                    if (submitted.length >= 3) ask.mutate({id: result.id, question: submitted});
                  }}
                >
                  <label htmlFor={'discovery-question-' + result.id}>Question about {result.title}</label>
                  <div>
                    <input
                      id={'discovery-question-' + result.id}
                      value={question}
                      maxLength={1_000}
                      autoFocus
                      onChange={(event) => setQuestion(event.target.value)}
                      placeholder="What does the posting say about compensation?"
                    />
                    <SecondaryButton type="submit" loading={ask.isPending} disabled={question.trim().length < 3}>
                      Get grounded answer
                    </SecondaryButton>
                  </div>
                </form>
              ) : null}
              {answers.get(result.id) ? (
                <div className="pd-discovery-answer" role="status">
                  <strong>Grounded answer</strong>
                  <p>{answers.get(result.id)!.answer}</p>
                  {answers.get(result.id)!.uncertainty.map((item) => <small key={item}>Unknown: {item}</small>)}
                  <span>Based only on the stored posting and deterministic fit evidence.</span>
                </div>
              ) : null}
            </div>
            <div className="pd-discovery-actions">
              <PrimaryButton
                loading={convert.isPending && convert.variables === result.id}
                leftSection={<BriefcaseBusiness size={16} aria-hidden="true" />}
                onClick={() => convert.mutate(result.id)}
              >
                Create opportunity
              </PrimaryButton>
              <SecondaryButton
                leftSection={<ThumbsUp size={16} aria-hidden="true" />}
                onClick={() => triage.mutate({id: result.id, state: 'interesting'})}
              >
                Interesting
              </SecondaryButton>
              <QuietButton
                aria-label={'Save ' + result.title}
                onClick={() => triage.mutate({id: result.id, state: 'saved'})}
              >
                <Bookmark size={17} aria-hidden="true" />
              </QuietButton>
              <QuietButton
                aria-label={'Ask about ' + result.title}
                aria-pressed={askTargetId === result.id}
                title="Ask a question answered only from stored posting evidence"
                onClick={() => {
                  setAskTargetId((current) => current === result.id ? null : result.id);
                  setQuestion('');
                }}
              >
                <CircleHelp size={17} aria-hidden="true" />
              </QuietButton>
              <QuietButton
                aria-label={'Dismiss ' + result.title}
                onClick={() => triage.mutate({id: result.id, state: 'dismissed'})}
              >
                <X size={17} aria-hidden="true" />
              </QuietButton>
            </div>
          </Surface>
        ))}
      </div>
      {ask.isError ? (
        <p className="pd-toast pd-toast--error" role="alert">
          {ask.error instanceof Error
            ? ask.error.message
            : 'The question could not be answered from the stored evidence.'}
        </p>
      ) : null}
      {triage.isError || convert.isError ? (
        <p className="pd-toast pd-toast--error" role="alert">
          {(triage.error ?? convert.error) instanceof Error
            ? (triage.error ?? convert.error as Error).message
            : 'That decision could not be saved.'}
        </p>
      ) : null}
    </>
  );
};
